import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Spinner from "@cloudscape-design/components/spinner";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import SpaceBetween from "@cloudscape-design/components/space-between";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import Modal from "@cloudscape-design/components/modal";
import Toggle from "@cloudscape-design/components/toggle";
import Button from "@cloudscape-design/components/button";
import Ec2InstanceDetail, { Ec2InstanceData } from "./Ec2InstanceDetail";
import { ec2InstanceUrl } from "./awsConsoleUrls";

export default function LiveEc2DetailPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [searchParams] = useSearchParams();
  const account = searchParams.get("account");
  const region = searchParams.get("region") || getDefaultRegion();

  const [instance, setInstance] = useState<Ec2InstanceData | null>(null);
  useDocumentTitle(instance?.name || instanceId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creds, setCreds] = useState<{ accessKeyId: string; secretAccessKey: string; sessionToken: string } | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const token = getAuthToken();
        if (!token) throw new Error("No auth token");

        const accountsRes = await apiFetch(`/accounts`);
        const { data } = await accountsRes.json();
        const acc = data.find((a: any) => a.documentId === account);
        if (!acc) throw new Error("Account not found");

        const resolvedCreds = getAwsCreds(acc.Alias);
        if (!resolvedCreds) throw new Error(`No AWS credentials for account "${acc.Alias}"`);
        setCreds(resolvedCreds);

        const res = await apiFetch(
          `/inventory/live/ec2/${instanceId}?region=${region}`,
          {
            headers: {
              "X-AWS-Access-Key-Id": resolvedCreds.accessKeyId,
              "X-AWS-Secret-Access-Key": resolvedCreds.secretAccessKey,
              "X-AWS-Session-Token": resolvedCreds.sessionToken,
            },
          }
        );
        if (!res.ok) throw new Error(`Failed to fetch instance: ${res.statusText}`);
        setInstance(await res.json());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [instanceId, account, region]);

  const temporaryOnEnabled = instance?.tags?.some(
    (t) => t.Key === "ICL_EC2_Temporary_TurnOn" && t.Value === "True"
  ) ?? false;

  async function handleToggle(checked: boolean) {
    if (!creds || !instance) return;
    const token = getAuthToken();
    if (!token) return;

    setToggling(true);
    try {
      const headers: Record<string, string> = {
        "X-AWS-Access-Key-Id": creds.accessKeyId,
        "X-AWS-Secret-Access-Key": creds.secretAccessKey,
        "X-AWS-Session-Token": creds.sessionToken,
      };
      const url = `/inventory/live/ec2/${instanceId}/tags?region=${region}`;

      if (checked) {
        await apiFetch(url, { method: "POST", headers, body: JSON.stringify({ key: "ICL_EC2_Temporary_TurnOn", value: "True" }) });
        setInstance({ ...instance, tags: [...(instance.tags || []), { Key: "ICL_EC2_Temporary_TurnOn", Value: "True" }] });
      } else {
        await apiFetch(url, { method: "DELETE", headers, body: JSON.stringify({ key: "ICL_EC2_Temporary_TurnOn" }) });
        setInstance({ ...instance, tags: (instance.tags || []).filter((t) => t.Key !== "ICL_EC2_Temporary_TurnOn") });
      }
    } finally {
      setToggling(false);
    }
  }

  async function handleInstanceState(action: "stop" | "start") {
    if (!creds || !instance) return;
    const token = getAuthToken();
    if (!token) return;
    const headers: Record<string, string> = {
      "X-AWS-Access-Key-Id": creds.accessKeyId,
      "X-AWS-Secret-Access-Key": creds.secretAccessKey,
      "X-AWS-Session-Token": creds.sessionToken,
    };
    await apiFetch(`/inventory/live/ec2/${instanceId}/${action}?region=${region}`, { method: "POST", headers });
    setInstance({ ...instance, state: action === "stop" ? "stopping" : "pending" });
  }

  if (loading) return <Spinner size="large" />;
  if (error) return <StatusIndicator type="error">{error}</StatusIndicator>;
  if (!instance) return null;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "EC2", href: "/live-inventory/ec2" },
          { text: instanceId || "", href: "" },
        ]}
      />
      <Header
        variant="h1"
        actions={
          <ButtonDropdown
            items={[
              { id: "temporary-on", text: "Temporary On" },
              {
                id: "instance-state",
                text: "Instance State",
                items: [
                  { id: "stop", text: "Stop", disabled: instance.state !== "running" },
                  { id: "start", text: "Start", disabled: instance.state !== "stopped" },
                ],
              },
              ...(instance.instanceId && region
                ? [
                    {
                      id: "aws-console",
                      text: "Open in AWS Console",
                      href: ec2InstanceUrl(instance.instanceId, region),
                      external: true,
                    },
                  ]
                : []),
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === "temporary-on") setModalVisible(true);
              if (detail.id === "stop") handleInstanceState("stop");
              if (detail.id === "start") handleInstanceState("start");
            }}
          >
            Actions
          </ButtonDropdown>
        }
      >
        {instance.name || instanceId}
      </Header>
      <Ec2InstanceDetail instance={instance} />
      <Modal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        header="Temporary On"
        footer={<Box float="right"><Button variant="primary" onClick={() => setModalVisible(false)}>Close</Button></Box>}
      >
        <SpaceBetween size="m">
          <div><Box variant="awsui-key-label">Instance ID</Box><div>{instance.instanceId}</div></div>
          <div><Box variant="awsui-key-label">Name</Box><div>{instance.name}</div></div>
          <Box variant="p">
            The 'Temporary On' feature marks this instance for automatic shutdown to save costs.
            When enabled, the tag ICL_EC2_Temporary_TurnOn=true is added to the instance.
            An automated process will turn off instances with this tag according to a schedule.
          </Box>
          <Toggle
            checked={temporaryOnEnabled}
            onChange={({ detail }) => handleToggle(detail.checked)}
            disabled={toggling}
          >
            {toggling ? "Updating..." : temporaryOnEnabled ? "Enabled" : "Disabled"}
          </Toggle>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
